import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";

import { deleteVenue, listVenues, updateVenueMarkup } from "@/features/ops/api/opsApi";
import { useOpsStore } from "@/features/ops/model/opsStore";
import type { VenueRow } from "@/features/ops/types";
import { ConfirmDialog } from "@/shared/ui";

export function VenueList() {
  const token = useOpsStore((s) => s.token)!;
  const qc = useQueryClient();
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState<VenueRow | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["ops", "venues"],
    queryFn: () => listVenues(token),
  });

  const update = useMutation({
    mutationFn: ({ id, markup }: { id: string; markup: string }) =>
      updateVenueMarkup(token, id, markup),
    onSuccess: (_, vars) => {
      setEdits((prev) => {
        const next = { ...prev };
        delete next[vars.id];
        return next;
      });
      qc.invalidateQueries({ queryKey: ["ops", "venues"] });
      // Markup feeds invoice totals — flush both ops and venue invoice
      // caches so the new percentage reflects everywhere it's read.
      qc.invalidateQueries({ queryKey: ["ops", "invoices"] });
      qc.invalidateQueries({ queryKey: ["invoices", "me"] });
    },
  });

  const remove = useMutation({
    mutationFn: (venueId: string) => deleteVenue(token, venueId),
    onSuccess: () => {
      setConfirmDelete(null);
      // A venue delete cascades to its postings, shifts, and invoices —
      // refresh every ops surface that could reference them.
      qc.invalidateQueries({ queryKey: ["ops", "venues"] });
      qc.invalidateQueries({ queryKey: ["ops", "upcoming-shifts"] });
      qc.invalidateQueries({ queryKey: ["ops", "payouts"] });
      qc.invalidateQueries({ queryKey: ["ops", "invoices"] });
    },
  });

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading...</div>;
  if (error) return <div className="text-sm text-destructive">Could not load venues.</div>;

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">Venues</h2>
      {data && data.length === 0 && (
        <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
          No venues yet.
        </div>
      )}
      <div className="space-y-2">
        {data?.map((v) => {
          const draft = edits[v.id];
          const dirty = draft !== undefined && draft !== v.markup_percentage;
          return (
            <div key={v.id} className="rounded-lg border bg-card p-4 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <p className="font-semibold">{v.business_name || "(no name)"}</p>
                  <p className="text-sm text-muted-foreground">{v.address}</p>
                  <div className="text-xs text-muted-foreground space-y-0.5">
                    <p>
                      <span className="font-medium">Account:</span>{" "}
                      {[v.owner_first_name, v.owner_last_name].filter(Boolean).join(" ") || "—"}
                      {" · "}
                      {v.owner_email}
                    </p>
                    <p>
                      <span className="font-medium">Primary contact:</span>{" "}
                      {[v.contact_first_name, v.contact_last_name].filter(Boolean).join(" ") || "—"}
                      {v.contact_role ? ` · ${v.contact_role}` : ""}
                      {v.contact_phone ? ` · ${v.contact_phone}` : ""}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-muted-foreground">Markup %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={draft ?? v.markup_percentage}
                    onChange={(e) => setEdits((p) => ({ ...p, [v.id]: e.target.value }))}
                    className="w-24 rounded-md border border-input bg-background px-2 py-1 text-sm"
                  />
                  <button
                    onClick={() => update.mutate({ id: v.id, markup: draft ?? v.markup_percentage })}
                    disabled={!dirty || update.isPending}
                    className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setConfirmDelete(v)}
                    aria-label={`Delete ${v.business_name || v.owner_email}`}
                    className="rounded-md border border-input p-1.5 text-destructive-foreground hover:bg-destructive-light"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        open={confirmDelete !== null}
        destructive
        title="Delete this venue?"
        description={
          <>
            This permanently deletes{" "}
            <span className="font-medium text-copy">
              {confirmDelete?.business_name || confirmDelete?.owner_email}
            </span>{" "}
            and all of its data — postings, shifts, and invoices. This cannot
            be undone.
          </>
        }
        confirmLabel="Delete permanently"
        loadingLabel="Deleting…"
        loading={remove.isPending}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete.id)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
